import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Delete from '@mui/icons-material/Delete'
import Edit from '@mui/icons-material/Edit'
import {
  Box,
  Button,
  Card,
  Link,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import { useStytchIsAuthorized } from '@stytch/react/b2b'
import useGetThemeColor from '@/hooks/useGetThemeColor'
import useLocationHelpers from '@/hooks/useLocationHelpers'
import useOverlay from '@/hooks/useOverlay'
import { useGetDocuments } from '@/service-library/hooks/documents'
import {
  useDeleteWorkflowState,
  useUpdateWorkflowState,
} from '@/service-library/hooks/workflow-states'
import queryKeys from '@/service-library/query-keys'
import { showErrorSnackbar, showSuccessSnackbar } from '@/utils/snackbars'
import CodeEditorDialog from '@/components/code-editor/CodeEditorDialog'
import CopyIDButton from '@/components/copy-id-button/CopyIDButton'
import LargeHeading from '@/components/large-heading/LargeHeading'
import PixyMarkdown from '@/components/markdown/PixyMarkdown'
import AddEditWorkflowStateDialog from '@/components/workflow-states-page/AddEditWorkflowStateDialog'
import { useSelectedWorkflowContext } from '@/components/workflows/SelectedWorkflowProvider'
import DeleteWorkflowStateDialog from './DeleteWorkflowStateDialog'
import WorkflowStateDocumentViews from './WorkflowStateDocumentViews'
import { useWorkflowStatePageContext } from './WorkflowStatePage'
import WorkflowStateRelevantFields from './WorkflowStateRelevantFields'

export default function WorkflowStateSettings() {
  const {
    workflowState,
    queryKey: detailQueryKey,
    listQueryKey,
  } = useWorkflowStatePageContext()
  const navigate = useNavigate()
  const { appendCurrentParamsToUrl } = useLocationHelpers()
  const { isAuthorized: canEditWorkflowState } = useStytchIsAuthorized(
    'pd.project',
    'edit',
  )
  const { selectedWorkflow } = useSelectedWorkflowContext()

  const { updateWorkflowState } = useUpdateWorkflowState({
    sideEffectQueryKeys: [
      queryKeys.workflows.lists(), // Update it in case they've already fetched it on other pages
    ],
    detailQueryKey,
    listQueryKey,
    onError: () => {
      showErrorSnackbar(
        'Unable to update workflow state. Please try again later.',
      )
    },
    onSuccess: () => {
      showSuccessSnackbar('Description Updated')
    },
  })

  const editOverlay = useOverlay()
  const deleteOverlay = useOverlay()

  const { documents, isPending } = useGetDocuments({
    filters: {
      workflow_states__id: workflowState.id,
      limit: '1',
      fields__only: 'id',
    },
  })

  const { deleteWorkflowState } = useDeleteWorkflowState({
    listQueryKey,
    sideEffectQueryKeys: [queryKeys.workflows.lists()],
    onError: () => {
      showErrorSnackbar(
        'Unable to delete workflow state. Please try again later.',
      )
    },
    onSuccess: () => {
      showSuccessSnackbar('Workflow State Deleted')
    },
  })

  const handleDeleteWorkflowState = () => {
    deleteWorkflowState(workflowState.id)
    navigate(appendCurrentParamsToUrl('../workflow-states'), {
      relative: 'path',
    })
  }

  const getThemeColor = useGetThemeColor()

  const isInitialState =
    selectedWorkflow?.initial_workflow_state_id === workflowState.id

  const hasDocuments = documents.length > 0

  const descriptionOverlay = useOverlay()
  const [descriptionValue, setDescriptionValue] = useState(
    workflowState.description || '',
  )

  return (
    <Stack spacing={4}>
      <LargeHeading
        heading={
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box
              sx={{
                background: getThemeColor(workflowState.color),
                width: 4,
                height: 24,
                borderRadius: 4,
              }}
            />
            <Box>{workflowState.name}</Box>
          </Stack>
        }
        actions={
          <>
            {canEditWorkflowState &&
              !['available_for_training', 'training_batch'].includes(
                workflowState.code,
              ) && (
                <>
                  <Button
                    variant="text"
                    startIcon={<Edit />}
                    onClick={editOverlay.open}
                  >
                    Edit
                  </Button>
                  <Tooltip
                    title={
                      <>
                        {isInitialState && (
                          <div>Cannot delete the initial workflow state.</div>
                        )}

                        {hasDocuments && (
                          <div>
                            Cannot delete workflow states that have documents
                            assigned to them.
                          </div>
                        )}
                      </>
                    }
                    slotProps={{
                      popper: {
                        sx: {
                          display:
                            hasDocuments || isInitialState ? undefined : 'none',
                        },
                      },
                    }}
                  >
                    {/* Tooltip automatically hides if it's main child is disabled, so we need to wrap it with a Box */}
                    <Box>
                      <Button
                        variant="text"
                        color="error"
                        startIcon={<Delete />}
                        onClick={deleteOverlay.open}
                        disabled={isPending || hasDocuments || isInitialState}
                      >
                        Delete
                      </Button>
                    </Box>
                  </Tooltip>
                </>
              )}
            <CopyIDButton stringToCopy={workflowState.id} />
          </>
        }
      />

      {isInitialState && (
        <Stack>
          <Typography variant="h6">Initial State</Typography>
          <Typography variant="body2" color="text.secondary">
            New documents will be uploaded directly to this workflow state.
          </Typography>
        </Stack>
      )}

      <WorkflowStateDocumentViews
        workflowState={workflowState}
        detailQueryKey={detailQueryKey}
      />
      <Stack spacing={1}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography variant="h6">Description</Typography>
          <Button
            variant="text"
            size="small"
            startIcon={<Edit />}
            onClick={descriptionOverlay.open}
          >
            Edit
          </Button>
        </Stack>
        <Card>
          <Box sx={{ p: 2, py: workflowState?.description ? 0 : 1.5 }}>
            {workflowState?.description ? (
              <PixyMarkdown>{workflowState?.description}</PixyMarkdown>
            ) : (
              <Typography variant="caption" color="text.secondary">
                <i>Click &quot;Edit&quot; to add a description.</i>
              </Typography>
            )}
          </Box>
        </Card>

        <CodeEditorDialog
          overlay={descriptionOverlay}
          title="Description"
          value={descriptionValue || ''}
          setValue={setDescriptionValue}
          wrapEnabledInitially
          characterLimit={8000}
          onSave={(updatedDescription) => {
            updateWorkflowState({
              ...workflowState,
              description: updatedDescription,
            })
            descriptionOverlay.close()
          }}
          description={
            <Typography variant="body2" color="text.secondary">
              This description will be available in various places throughout
              the app. Workflow state descriptions accept{' '}
              <Link
                href="https://www.markdownguide.org/cheat-sheet/"
                target="_blank"
                rel="noreferrer"
              >
                markdown formatting
              </Link>
              .
            </Typography>
          }
          mode="markdown"
        />
      </Stack>

      <WorkflowStateRelevantFields
        excludedFieldsIds={workflowState.excluded_project_grid_fields_ids || []}
        detailQueryKey={detailQueryKey}
        workflowState={workflowState}
      />

      <DeleteWorkflowStateDialog
        overlay={deleteOverlay}
        workflowState={workflowState}
        onDelete={handleDeleteWorkflowState}
      />
      <AddEditWorkflowStateDialog
        overlay={editOverlay}
        workflowId={workflowState.workflow_id}
        workflowState={workflowState}
        detailQueryKey={detailQueryKey}
        listQueryKey={listQueryKey}
      />
    </Stack>
  )
}
